import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Container,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Card,
  CardContent,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import CalculateIcon from "@mui/icons-material/Calculate";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import WizualizacjaArkusza from "./components/WizualizacjaArkusza";
import Statystyki from "./components/Statystyki";

const API_URL =
  process.env.NODE_ENV === "development" ? "http://localhost:5000/api" : "/api";

function App() {
  const [arkusz, setArkusz] = useState({ szerokosc: 1500, wysokosc: 3000 });
  const [formatki, setFormatki] = useState([
    { id: 1, szerokosc: 400, wysokosc: 600, ilosc: 2, nazwa: "Format A" },
    { id: 2, szerokosc: 300, wysokosc: 500, ilosc: 3, nazwa: "Format B" },
  ]);
  const [nowaFormatka, setNowaFormatka] = useState({
    szerokosc: "",
    wysokosc: "",
    ilosc: 1,
    nazwa: "",
  });
  const [wyniki, setWyniki] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const dodajFormatke = () => {
    if (
      !nowaFormatka.szerokosc ||
      !nowaFormatka.wysokosc ||
      !nowaFormatka.ilosc
    ) {
      setSnackbar({
        open: true,
        message: "Wypełnij wymagane pola",
        severity: "error",
      });
      return;
    }

    const nowa = {
      id: Date.now(),
      // user inputs in cm -> convert to mm for internal representation
      szerokosc: Math.round((parseFloat(nowaFormatka.szerokosc) || 0) * 10),
      wysokosc: Math.round((parseFloat(nowaFormatka.wysokosc) || 0) * 10),
      ilosc: parseInt(nowaFormatka.ilosc),
      nazwa: nowaFormatka.nazwa || `Format ${formatki.length + 1}`,
    };

    setFormatki([...formatki, nowa]);
    setNowaFormatka({ szerokosc: "", wysokosc: "", ilosc: 1, nazwa: "" });
    setSnackbar({
      open: true,
      message: "Dodano formatkę",
      severity: "success",
    });
  };

  const usunFormatke = (id) => {
    setFormatki(formatki.filter((f) => f.id !== id));
  };

  const optymalizuj = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API_URL}/optymalizuj`, {
        arkusz: arkusz,
        formatki: formatki.map((f) => ({
          szerokosc: f.szerokosc,
          wysokosc: f.wysokosc,
          ilosc: f.ilosc,
          nazwa: f.nazwa,
        })),
        metoda: "maxrects",
      });

      if (response.data.success) {
        setWyniki(response.data.wyniki);
        setSnackbar({
          open: true,
          message: "Optymalizacja zakończona sukcesem",
          severity: "success",
        });
      } else {
        setError(response.data.error || "Wystąpił błąd podczas optymalizacji");
      }
    } catch (err) {
      setError("Błąd połączenia z serwerem: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const generujPDF = () => {
    // Tutaj można dodać generowanie PDF
    setSnackbar({
      open: true,
      message: "Funkcja generowania PDF w przygotowaniu",
      severity: "info",
    });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography
        variant="h3"
        component="h1"
        gutterBottom
        align="center"
        color="primary"
      >
        🛠️ Optymalizator Rozkładu Formatów
      </Typography>

      <Grid container spacing={3}>
        {/* Lewa kolumna - Konfiguracja */}
        <Grid item xs={12} md={5}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              ⚙️ Konfiguracja
            </Typography>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom color="primary">
                Arkusz materiału
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Szerokość (cm)"
                    type="number"
                    value={arkusz.szerokosc / 10}
                    onChange={(e) =>
                      setArkusz({
                        ...arkusz,
                        // store internally in mm
                        szerokosc: Math.round(
                          (parseFloat(e.target.value) || 0) * 10,
                        ),
                      })
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Wysokość (cm)"
                    type="number"
                    value={arkusz.wysokosc / 10}
                    onChange={(e) =>
                      setArkusz({
                        ...arkusz,
                        // store internally in mm
                        wysokosc: Math.round(
                          (parseFloat(e.target.value) || 0) * 10,
                        ),
                      })
                    }
                  />
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom color="primary">
                Dodaj nową formatkę
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Szer. (cm)"
                    type="number"
                    value={nowaFormatka.szerokosc}
                    onChange={(e) =>
                      setNowaFormatka({
                        ...nowaFormatka,
                        szerokosc: e.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Wys. (cm)"
                    type="number"
                    value={nowaFormatka.wysokosc}
                    onChange={(e) =>
                      setNowaFormatka({
                        ...nowaFormatka,
                        wysokosc: e.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Ilość"
                    type="number"
                    value={nowaFormatka.ilosc}
                    onChange={(e) =>
                      setNowaFormatka({
                        ...nowaFormatka,
                        ilosc: e.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Nazwa"
                    value={nowaFormatka.nazwa}
                    onChange={(e) =>
                      setNowaFormatka({
                        ...nowaFormatka,
                        nazwa: e.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<AddCircleIcon />}
                    onClick={dodajFormatke}
                    sx={{ mt: 1 }}
                  >
                    Dodaj Formatkę
                  </Button>
                </Grid>
              </Grid>
            </Box>

            <Box>
              <Typography variant="h6" gutterBottom color="primary">
                Lista formatów
              </Typography>
              <List sx={{ maxHeight: 300, overflow: "auto" }}>
                {formatki.map((formatka) => (
                  <React.Fragment key={formatka.id}>
                    <ListItem
                      secondaryAction={
                        <IconButton
                          edge="end"
                          onClick={() => usunFormatke(formatka.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={`${formatka.nazwa} - ${formatka.szerokosc / 10}×${formatka.wysokosc / 10} cm`}
                        secondary={`Ilość: ${formatka.ilosc}`}
                      />
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>
            </Box>

            <Box sx={{ mt: 3 }}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                startIcon={<CalculateIcon />}
                onClick={optymalizuj}
                disabled={loading || formatki.length === 0}
                sx={{ py: 1.5 }}
              >
                {loading ? "Optymalizuję..." : "Uruchom Optymalizację"}
              </Button>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                color="secondary"
                startIcon={<PictureAsPdfIcon />}
                onClick={generujPDF}
                disabled={!wyniki}
              >
                Generuj PDF z wynikami
              </Button>
            </Box>
          </Paper>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Grid>

        {/* Prawa kolumna - Wyniki i wizualizacja */}
        <Grid item xs={12} md={7}>
          <Paper elevation={3} sx={{ p: 3, height: "100%" }}>
            <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)}>
              <Tab label="📊 Wizualizacja" />
              <Tab label="📈 Statystyki" />
              <Tab label="📋 Szczegóły" />
            </Tabs>

            <Box sx={{ mt: 3 }}>
              {activeTab === 0 && wyniki && (
                <WizualizacjaArkusza arkusz={arkusz} wyniki={wyniki} />
              )}

              {activeTab === 1 && wyniki && <Statystyki wyniki={wyniki} />}

              {activeTab === 2 && wyniki && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Szczegółowe wyniki
                  </Typography>
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="h6" color="primary">
                        Podsumowanie
                      </Typography>
                      <Typography>
                        Wykorzystanie materiału:{" "}
                        <strong>
                          {wyniki.statystyki.procent_wykorzystania}%
                        </strong>
                      </Typography>
                      <Typography>
                        Liczba arkuszy:{" "}
                        <strong>{wyniki.statystyki.liczba_arkuszy}</strong>
                      </Typography>
                      <Typography>
                        Liczba formatów:{" "}
                        <strong>{wyniki.statystyki.liczba_formatow}</strong>
                      </Typography>
                    </CardContent>
                  </Card>

                  {wyniki.arkusze.map((arkusz, index) => (
                    <Card key={index} sx={{ mb: 2 }}>
                      <CardContent>
                        <Typography variant="h6">
                          Arkusz {index + 1} (wykorzystanie:{" "}
                          {arkusz.wykorzystanie}%)
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Formatów na arkuszu: {arkusz.formatki.length}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}

              {!wyniki && (
                <Box sx={{ textAlign: "center", py: 10 }}>
                  <Typography variant="h6" color="text.secondary">
                    Brak wyników. Uruchom optymalizację, aby zobaczyć
                    wizualizację.
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Container>
  );
}

export default App;
